/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/**
 * When the value of a routed command is cleared after the route is checked
 * ALWAYS  - cleared even when the active tab has no route for the command
 * ROUTED  - cleared only when the active tab has a route for the command
 * NEVER   - kept, the command is forwarded again when the tab of the command url changes
 */
export const ROUTE_CLEAR = {
  ALWAYS: 'ALWAYS',
  ROUTED: 'ROUTED',
  NEVER: 'NEVER',
};
/**
 * Find where a command is forwarded for the active tab.
 *
 * A route entry is
 * {
 *   event  - generic command event, ex: SET_BREAKDOWN
 *   routes - list of [tab names, route]; route is a UiController setter name or
 *            a function (value, dispatch) for the commands that are not re-emitted
 *   value  - (value) => value passed to the setter, optional
 *   clear  - ROUTE_CLEAR value, ALWAYS when it is not given
 * }
 *
 * @param {Object} entry - route entry of the command
 * @param {Number} tabValue - active tab value
 * @param {Object} tabList - tab name to tab value, ex: { SUMMARY: 0, PACE: 3 }
 * @returns {{route: String|Function|null, clear: Boolean}}
 */
export const getCommandRoute = (entry, tabValue, tabList) => {
  const match = entry.routes.find(([tabNames]) =>
    // tab names of null stands for every tab
    tabNames === null ? true : tabNames.some((name) => tabList?.[name] === tabValue)
  );
  const route = match ? match[1] : null;
  const rule = entry.clear ?? ROUTE_CLEAR.ALWAYS;
  return {
    route,
    clear: rule === ROUTE_CLEAR.ALWAYS || (rule === ROUTE_CLEAR.ROUTED && route !== null),
  };
};
